import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import {
  Box,
  Button,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Typography,
} from "@mui/material";
import {
  FileDownloadOutlined as ExportIcon,
  Refresh as RefreshIcon,
} from "@mui/icons-material";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import { Workbook } from "exceljs";
import { saveAs } from "file-saver";
import { toast } from "react-hot-toast";
import { PaginatedTable } from "../../../components";
import { ReceiptDetail } from "../../../components/reports/ReceiptDetail";
import { baseAxios, Context } from "../../../utils/config";
import { colors } from "../..";

const MAX_RANGE_DAYS = 366;

const GROUP_BY_OPTIONS = [
  { value: "stylist", label: "By Stylist" },
  { value: "client", label: "By Client" },
  { value: "service", label: "By Service" },
];

const SUMMARY_CARDS = [
  { key: "totalRevenue", label: "Total Revenue", money: true },
  { key: "totalTips", label: "Total Tips", money: true },
  { key: "totalTransactions", label: "Total Transactions", money: false },
  { key: "dateRange", label: "Report Period", money: false },
];

const dateFieldSx = {
  minWidth: 160,
  "& .MuiOutlinedInput-root": {
    backgroundColor: "background.paper",
    borderRadius: "6px",
  },
};

function formatDateToISO(date) {
  if (!date || !dayjs(date).isValid()) return "";
  return dayjs(date).format("YYYY-MM-DD");
}

function formatMoney(value) {
  const n = Number(value);
  if (Number.isNaN(n) || n < 0) return "$0.00";
  return `$${n.toFixed(2)}`;
}

function validateDateRange(startDate, endDate) {
  if (
    !startDate ||
    !endDate ||
    !dayjs(startDate).isValid() ||
    !dayjs(endDate).isValid()
  ) {
    toast.error("Please select both start and end dates", { duration: 3000 });
    return false;
  }
  if (dayjs(startDate).isAfter(dayjs(endDate), "day")) {
    toast.error("Start date must be before or equal to end date", {
      duration: 3000,
    });
    return false;
  }
  const daysDiff = dayjs(endDate).diff(dayjs(startDate), "day");
  if (daysDiff > MAX_RANGE_DAYS) {
    toast.error(`Date range cannot exceed ${MAX_RANGE_DAYS} days`, {
      duration: 3000,
    });
    return false;
  }
  return true;
}

function compareRows(a, b, field) {
  const va = a[field];
  const vb = b[field];
  if (typeof va === "number" || typeof vb === "number") {
    return (Number(va) || 0) - (Number(vb) || 0);
  }
  return String(va ?? "").localeCompare(String(vb ?? ""), undefined, {
    sensitivity: "base",
  });
}

function mapApiError(error) {
  if (error.response?.status === 403) {
    return "Access denied. You don't have permission to view this report.";
  }
  if (error.response?.status === 400) {
    return error.response.data?.message || "Invalid request parameters";
  }
  if (error.response?.status === 404) {
    return "Sales report endpoint not found";
  }
  if (error.response?.status === 500) {
    return "Server error. Please try again later.";
  }
  if (error.message === "Network Error") {
    return "Network error. Please check your connection and try again.";
  }
  return (
    error.response?.data?.message ||
    "Failed to load sales report. Please try again."
  );
}

const SalesReport = () => {
  const { location: globalLocation } = useContext(Context);

  const [groupBy, setGroupBy] = useState("stylist");
  const [startDate, setStartDate] = useState(dayjs().subtract(30, "day"));
  const [endDate, setEndDate] = useState(dayjs());
  const [rows, setRows] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(false);
  const [hasRun, setHasRun] = useState(false);
  const [sortBy, setSortBy] = useState("totalRevenue");
  const [sortOrder, setSortOrder] = useState("desc");

  const loadReport = useCallback(async () => {
    if (!validateDateRange(startDate, endDate)) return;

    setLoading(true);
    setHasRun(true);
    try {
      const response = await baseAxios.get("/sales-reports", {
        params: {
          groupBy,
          startDate: formatDateToISO(startDate),
          endDate: formatDateToISO(endDate),
        },
      });

      if (!response.data) {
        toast.error("No data returned from server", { duration: 3000 });
        setRows([]);
        setSummary(null);
        return;
      }

      const { groupBy: responseGroupBy, summary: nextSummary, data } =
        response.data;

      if (responseGroupBy && responseGroupBy !== groupBy) {
        console.warn(
          `Expected groupBy=${groupBy}, got groupBy=${responseGroupBy}`
        );
      }

      setSummary(nextSummary || null);

      if (!Array.isArray(data)) {
        setRows([]);
        return;
      }

      const transformed = data.map((group, index) => {
        let keyBase;
        if (groupBy === "stylist") {
          keyBase = group.stylistId ?? "unassigned";
        } else if (groupBy === "client") {
          keyBase = group.clientId ?? "walkin";
        } else {
          keyBase = group.serviceId ?? "unknown";
        }

        return {
          ...group,
          groupKey: `${groupBy}-${keyBase}-${index}`,
          totalRevenue: Number(group.totalRevenue || 0),
          totalTips: Number(group.totalTips || 0),
          transactionCount: Number(group.transactionCount || 0),
          visitCount: Number(group.visitCount || 0),
        };
      });

      setRows(transformed);
    } catch (error) {
      setRows([]);
      setSummary(null);
      toast.error(mapApiError(error), { duration: 5000 });
    } finally {
      setLoading(false);
    }
  }, [endDate, groupBy, startDate, globalLocation?.location_id]);

  useEffect(() => {
    loadReport();
  }, [loadReport]);

  const sortedRows = useMemo(() => {
    const copy = [...rows];
    copy.sort((a, b) => {
      const c = compareRows(a, b, sortBy);
      return sortOrder === "asc" ? c : -c;
    });
    return copy;
  }, [rows, sortBy, sortOrder]);

  const handleSortChange = useCallback((field, order) => {
    setSortBy(field);
    setSortOrder(order);
  }, []);

  const noopPagination = useCallback(() => {}, []);

  const handleGroupByChange = (event) => {
    setGroupBy(event.target.value);
    setSortBy("totalRevenue");
    setSortOrder("desc");
  };

  const handleExport = useCallback(async () => {
    if (!sortedRows.length) {
      toast.error("No data to export", { duration: 2500 });
      return;
    }

    try {
      const workbook = new Workbook();
      const worksheet = workbook.addWorksheet("Sales Report");
      const groupByLabel =
        GROUP_BY_OPTIONS.find((opt) => opt.value === groupBy)?.label || groupBy;

      if (groupBy === "stylist") {
        worksheet.columns = [
          { header: "Stylist", key: "stylistName", width: 22 },
          { header: "Total Revenue", key: "totalRevenue", width: 14 },
          { header: "Total Tips", key: "totalTips", width: 12 },
          { header: "Transactions", key: "transactionCount", width: 14 },
        ];
      } else if (groupBy === "client") {
        worksheet.columns = [
          { header: "Client", key: "clientName", width: 22 },
          { header: "Total Revenue", key: "totalRevenue", width: 14 },
          { header: "Total Tips", key: "totalTips", width: 12 },
          { header: "Visits", key: "visitCount", width: 12 },
        ];
      } else {
        worksheet.columns = [
          { header: "Service", key: "serviceName", width: 22 },
          { header: "Total Revenue", key: "totalRevenue", width: 14 },
          { header: "Proportional Tips", key: "totalTips", width: 16 },
          { header: "Times Performed", key: "transactionCount", width: 16 },
        ];
      }

      worksheet.getRow(1).font = { bold: true };
      sortedRows.forEach((row) => worksheet.addRow(row));

      const buffer = await workbook.xlsx.writeBuffer();
      saveAs(
        new Blob([buffer], {
          type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        }),
        `SalesReport_${groupByLabel.replace(/\s+/g, "")}_${dayjs().format("YYYY-MM-DD")}.xlsx`
      );
    } catch {
      toast.error("Export failed", { duration: 3000 });
    }
  }, [groupBy, sortedRows]);

  const columns = useMemo(() => {
    if (groupBy === "client") {
      return [
        {
          id: "clientName",
          label: "Client",
          field: "clientName",
          minWidth: 180,
          render: (v) => v || "Walk-in",
        },
        {
          id: "totalRevenue",
          label: "Total Revenue",
          field: "totalRevenue",
          minWidth: 140,
          align: "right",
          render: (v) => formatMoney(v),
        },
        {
          id: "totalTips",
          label: "Total Tips",
          field: "totalTips",
          minWidth: 120,
          align: "right",
          render: (v) => formatMoney(v),
        },
        {
          id: "visitCount",
          label: "Visits",
          field: "visitCount",
          minWidth: 100,
          align: "center",
          render: (v) => Number(v) || 0,
        },
      ];
    }

    if (groupBy === "service") {
      return [
        {
          id: "serviceName",
          label: "Service",
          field: "serviceName",
          minWidth: 200,
          render: (v) => v || "—",
        },
        {
          id: "totalRevenue",
          label: "Total Revenue",
          field: "totalRevenue",
          minWidth: 140,
          align: "right",
          render: (v) => formatMoney(v),
        },
        {
          id: "totalTips",
          label: "Proportional Tips",
          field: "totalTips",
          minWidth: 150,
          align: "right",
          render: (v) => formatMoney(v),
        },
        {
          id: "transactionCount",
          label: "Times Performed",
          field: "transactionCount",
          minWidth: 140,
          align: "center",
          render: (v) => Number(v) || 0,
        },
      ];
    }

    return [
      {
        id: "stylistName",
        label: "Stylist",
        field: "stylistName",
        minWidth: 180,
        render: (v) => v || "Unassigned",
      },
      {
        id: "totalRevenue",
        label: "Total Revenue",
        field: "totalRevenue",
        minWidth: 140,
        align: "right",
        render: (v) => formatMoney(v),
      },
      {
        id: "totalTips",
        label: "Total Tips",
        field: "totalTips",
        minWidth: 120,
        align: "right",
        render: (v) => formatMoney(v),
      },
      {
        id: "transactionCount",
        label: "Transactions",
        field: "transactionCount",
        minWidth: 130,
        align: "center",
        render: (v) => Number(v) || 0,
      },
    ];
  }, [groupBy]);

  const renderDetail = useCallback(
    (row) => <ReceiptDetail data={row} groupBy={groupBy} />,
    [groupBy]
  );

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Box sx={{ px: 8, py: 3 }}>
        <Box
          sx={{
            display: "flex",
            alignItems: "flex-start",
            justifyContent: "space-between",
            flexWrap: "wrap",
            gap: 2,
            mt: 1.5,
            mb: 2,
          }}
        >
          <Box>
            <Typography fontWeight={600} sx={{ fontSize: "1.5rem" }}>
              Sales Report
            </Typography>
            <Typography variant="body2" color="text.secondary">
              View sales data grouped by stylist, client, or service with
              receipt-level details.
            </Typography>
          </Box>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1.5,
              flexWrap: "wrap",
            }}
          >
            <FormControl
              size="small"
              sx={{
                minWidth: 140,
                "& .MuiOutlinedInput-root": {
                  backgroundColor: "background.paper",
                  borderRadius: "6px",
                },
              }}
            >
              <InputLabel id="sales-group-by">Group by</InputLabel>
              <Select
                labelId="sales-group-by"
                label="Group by"
                value={groupBy}
                onChange={handleGroupByChange}
              >
                {GROUP_BY_OPTIONS.map((opt) => (
                  <MenuItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <DatePicker
              label="Start date"
              value={startDate}
              onChange={(value) => setStartDate(value)}
              slotProps={{ textField: { size: "small", sx: dateFieldSx } }}
            />
            <DatePicker
              label="End date"
              value={endDate}
              onChange={(value) => setEndDate(value)}
              maxDate={dayjs()}
              slotProps={{ textField: { size: "small", sx: dateFieldSx } }}
            />
            <IconButton
              aria-label="Refresh report"
              onClick={loadReport}
              disabled={loading}
              size="medium"
              sx={{
                border: "1px solid",
                borderColor: "divider",
                bgcolor: "background.paper",
                borderRadius: "6px",
                "&:hover": { bgcolor: "grey.100" },
              }}
            >
              <RefreshIcon />
            </IconButton>
            <Button
              variant="outlined"
              color="secondary"
              startIcon={<ExportIcon />}
              onClick={handleExport}
              disabled={loading || !sortedRows.length}
              sx={{
                minHeight: 40,
                textTransform: "none",
                fontWeight: 600,
                borderRadius: "6px",
              }}
            >
              Export
            </Button>
            <Button
              variant="contained"
              color="primary"
              onClick={loadReport}
              disabled={loading}
              sx={{
                minHeight: 40,
                px: 2,
                textTransform: "none",
                fontWeight: 700,
                borderRadius: "6px",
                boxShadow: "none",
                "&:hover": { boxShadow: "none" },
              }}
            >
              {loading ? "Loading…" : "Run Report"}
            </Button>
          </Box>
        </Box>

        {summary && (
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: {
                xs: "1fr",
                sm: "1fr 1fr",
                md: "repeat(4, 1fr)",
              },
              gap: 1.5,
              mb: 2.5,
            }}
          >
            {SUMMARY_CARDS.map(({ key, label, money }) => {
              let valueNode;
              if (key === "dateRange") {
                valueNode = (
                  <>
                    <Typography
                      fontWeight={700}
                      sx={{ fontSize: "1rem", lineHeight: 1.3 }}
                    >
                      {summary.dateRange?.startDate} to{" "}
                      {summary.dateRange?.endDate}
                    </Typography>
                    {summary.locationFilter ? (
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ display: "block", mt: 0.5 }}
                      >
                        {summary.locationFilter}
                      </Typography>
                    ) : null}
                  </>
                );
              } else if (money) {
                valueNode = (
                  <Typography
                    fontWeight={700}
                    sx={{ fontSize: "1.75rem", lineHeight: 1.1 }}
                  >
                    {formatMoney(summary[key])}
                  </Typography>
                );
              } else {
                valueNode = (
                  <Typography
                    fontWeight={700}
                    sx={{ fontSize: "1.75rem", lineHeight: 1.1 }}
                  >
                    {Number(summary[key]) || 0}
                  </Typography>
                );
              }

              return (
                <Box
                  key={key}
                  sx={{
                    border: "1px solid",
                    borderColor: "divider",
                    borderRadius: "6px",
                    bgcolor: "background.paper",
                    overflow: "hidden",
                  }}
                >
                  <Box
                    sx={{
                      px: 2,
                      py: 0.75,
                      bgcolor: "secondary.main",
                      borderBottom: "1px solid",
                      borderColor: "primary.main",
                    }}
                  >
                    <Typography
                      variant="caption"
                      sx={{
                        color: colors.gold,
                        fontWeight: 700,
                        letterSpacing: 0.4,
                        textTransform: "uppercase",
                      }}
                    >
                      {label}
                    </Typography>
                  </Box>
                  <Box sx={{ px: 2, py: 1.5 }}>{valueNode}</Box>
                </Box>
              );
            })}
          </Box>
        )}

        <PaginatedTable
          key={groupBy}
          columns={columns}
          rows={sortedRows}
          totalRowCount={sortedRows.length}
          loading={loading}
          onPaginationChange={noopPagination}
          onSortChange={handleSortChange}
          initialOrderBy="totalRevenue"
          initialOrder="desc"
          searchQuery={`${groupBy}|${formatDateToISO(startDate)}|${formatDateToISO(endDate)}|${globalLocation?.location_id ?? "all"}`}
          clientSidePagination
          skeletonHeight="40px"
          getRowKey={(row) => row.groupKey}
          renderDetail={renderDetail}
          emptyMessage={
            hasRun
              ? "No sales data for this date range"
              : "Click Run Report to load sales data"
          }
        />
      </Box>
    </LocalizationProvider>
  );
};

export default SalesReport;
